import { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { ROUTES } from '@/lib/routes';
import { AutomationsShellView } from './AutomationsShellView';
import { useTakeoverEscape } from './useTakeoverEscape';

/**
 * Layout route for the Automations takeover: every automations-surface page
 * (automation list, detail, editor, runs, replays) renders inside this
 * shell, and the app shell drops its sidebar on these paths (see
 * `isAutomationsPath`). Back and `esc` return to the org's agent sessions
 * page — except on the editor canvas, where `esc` keeps its local meaning
 * (deselect) and only the chip leaves.
 */
export function AutomationsShell() {
  const slug = useActiveOrgSlug();
  const { activeMembership } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = useCallback(() => {
    const fallback = slug ? ROUTES.org(slug).runs : ROUTES.root;
    void navigate(fallback);
  }, [navigate, slug]);

  // The editor claims `esc` for canvas selection; exiting mid-edit by a
  // reflex-press would be destructive to flow, so only the chip leaves.
  // Match both canonical `/automations` and legacy `/flows` editor URLs
  // so esc stays local across the alias redirect hop.
  const onEditorCanvas = useMemo(
    () => /\/(?:automations|flows)\/(new$|[^/]+\/edit$)/.test(location.pathname),
    [location.pathname],
  );
  useTakeoverEscape(goBack, !onEditorCanvas);

  return (
    <AutomationsShellView
      orgName={activeMembership?.organization.name ?? 'Organization'}
      activePath={location.pathname}
      automationsTo={ROUTES.org(slug).automations}
      runsTo={ROUTES.org(slug).automationsRuns}
      onBack={goBack}
    >
      <Outlet />
    </AutomationsShellView>
  );
}
